import {useMutation, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList, type PagedListParams} from '@/hooks/common/use-paged-list'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

export type FilaCargos = components['schemas']['FilaCargosRead']
export type CargoDeFila = components['schemas']['CargoDeFilaRead']
/** `total_con_aviso` viaja con las filas: el chip «Con aviso» lo necesita en la misma lectura. */
export type PaginaCargos = components['schemas']['PaginaCargos']

export const MATRIZ_CARGOS_PAGE_SIZE = 15

function base(businessId: string | undefined) {
  return `/businesses/${businessId}/sgsst/matriz-riesgos/cargos`
}

function cargosKey(businessId: string | undefined) {
  return ['matriz-cargos', businessId]
}

/** El backend pagina, pero calcula sobre todo el censo: los cargos de una fila dependen de él. */
export function useMatrizCargos(businessId: string | undefined, params: PagedListParams, enabled = true) {
  return usePagedList<FilaCargos, PaginaCargos>(
    cargosKey(businessId),
    businessId && enabled ? base(businessId) : undefined,
    params,
    MATRIZ_CARGOS_PAGE_SIZE
  )
}

/** Silencia el aviso en su estado de hoy; exige motivo y queda registrado quién lo ignoró. */
export function useIgnorarAviso(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {riesgoFilaId: string; motivo: string}) =>
      (
        await api.post<FilaCargos>(`${base(businessId)}/avisos/${input.riesgoFilaId}/ignorar`, {
          motivo: input.motivo,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: cargosKey(businessId)})
      track('matriz_cargo_aviso_ignorado', {business_id: businessId})
    },
  })
}

/** Acto de firmante como el que lo calló; la constancia se borra en blando. */
export function useVolverAMostrarAviso(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {riesgoFilaId: string}) =>
      (await api.delete<FilaCargos>(`${base(businessId)}/avisos/${input.riesgoFilaId}/ignorar`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: cargosKey(businessId)})
      track('matriz_cargo_aviso_vuelve', {business_id: businessId})
    },
  })
}
